import DoubleQuotes from "@/components/case-study/mobifinx/svg/DoubleQuotes";
import Container from "@/components/common/Container";
import Image from "next/image";
import Title from "./common/Title";

const About = () => (
  <Container className="py-20 xl:py-36 bg-black-pearl">
    <Title
      className="pb-20 xl:pb-36 pl-0 md:pl-16 xl:pl-24 w-full md:w-4/5 xl:w-3/4"
      customClass="pb-6 xl:pb-10"
      Description="In this project, the major challenge was to achieve a harmonious synergy between UI/UX design and web development as the client was very precise about his expectations. Meeting the needs of experienced traders while maintaining user-friendliness and integrating real-time data feeds and advanced trading tools required careful consideration. Our team's collaborative efforts between designers and developers were crucial in overcoming these challenges. The result is a platform that strikes the perfect balance between aesthetics, functionality, and user engagement, thanks to innovative design solutions."
      SubTitle="Challenges"
    />
    <Title
      className="pb-20 xl:pb-36 pl-0 md:pl-16 xl:pl-24 w-full md:w-4/5 xl:w-3/4"
      customClass="pb-6 xl:pb-10"
      Description="To ensure an optimal user experience for our blockchain-based digital asset trading platform, we conducted a thorough UX research using the combination of qualitative and quantitative methods. We started by conducting user interviews and surveys to gain valuable insights into traders' needs, pain points, and preferences. This qualitative data served as the foundation for developing usability tests and interactive prototypes, enabling us to iterate and enhance the platform's interface. Based on the findings from our UX research, we identified the end users' challenges and created a user persona that accurately represented the target user group."
      SubTitle="Research"
    />
    <Container large>
      <div className="rounded-2xl py-10 px-6 md:py-10 md:px-16 xl:py-20 xl:px-24 bg-[#121834]">
        <h3 className="text-2xl md:text-4xl font-normal text-white pb-6 md:pb-10">
          <span className="font-jost">User Persona</span>
        </h3>
        <div className="flex flex-col md:flex-row md:justify-between md:gap-4 lg:gap-0 pb-10 md:pb-6 xl:pb-20">
          <div className="w-full md:w-4/12 pb-10 md:pb-0">
            <div className="relative">
              <div className="fs-0">
                <Image
                  alt="userprofile"
                  height={569}
                  src={"/assets/case-study/mobifinx/user-profile.webp"}
                  width={393}
                />
              </div>
              <div className="rounded-2xl absolute bottom-8 left-8 md:bottom-4 md:left-4 xl:bottom-8 xl:left-8">
                <h4 className="text-xl md:text-base xl:text-3xl font-bold text-white mb-2">
                  <span className="font-jost">Bill Luise</span>
                </h4>
                <p className="text-base md:text-sm xl:text-lg font-bold text-white">
                  <span className="font-jost">Crypto Trader</span>
                </p>
              </div>
            </div>
          </div>
          <div className="w-full md:w-7/12">
            <DoubleQuotes className="mb-4 w-1/12 md:w-[10%] xl:w-fit" />
            <h4 className="text-xl md:text-base xl:text-3xl  font-medium text-white">
              <span className="font-jost">
                Turning blockchain insights into trading success, one coin at a
                time.
              </span>
            </h4>
            <div className="flex justify-end pb-10 md:pb-6 xl:pb-20">
              <DoubleQuotes className="mb-4 w-1/12 md:w-[10%] xl:w-fit rotate-180" />
            </div>
            <h4 className="text-xl md:text-3xl font-medium text-white pb-6 xl:pb-2 mb-0">
              <span className="font-jost">Background</span>
            </h4>
            <p className="text-lg md:text-base xl:text-lg font-normal text-white text-justify">
              <span className="font-jost">
                Bill is a 32-year-old finance professional, He is a seasoned
                cryptocurrency trader with five years of active experience in
                the digital asset market. With a deep understanding of
                blockchain technology, he navigates the volatile market with an
                awareness of its potential risks and rewards. Bill dedicates
                ample time to researching market trends and analyzing
                cryptocurrencies before making trading decisions. As a
                tech-savvy individual, he values secure and efficient platforms
                that enable swift trade execution.
              </span>
            </p>
          </div>
        </div>
        <div className="flex flex-col md:flex-row">
          <div className="w-full md:w-1/2 lg:w-7/12">
            <h4 className="text-lg md:text-3xl font-bold text-white pb-6 xl:pb-10">
              <span className="font-jost">Goals</span>
            </h4>
            <ul className="list-none mb-0">
              <li className="text-lg font-normal text-white pb-7 md:pb-8 xl:pb-10 md:pr-12 lg:pr-0">
                <span className="font-jost">
                  Stay updated with real-time market data.
                </span>
              </li>
              <li className="text-lg font-normal text-white pb-7 md:pb-8 xl:pb-10 md:pr-12 lg:pr-0">
                <span className="font-jost">
                  Execute trades swiftly to capitalize on market fluctuations.
                </span>
              </li>
              <li className="text-lg font-normal text-white pb-7 md:pb-8 xl:pb-10 md:pr-12 lg:pr-0">
                <span className="font-jost">
                  Diversify his cryptocurrency portfolio based.
                </span>
              </li>
              <li className="text-lg font-normal text-white pb-10 md:pb-0 md:pr-12 lg:pr-0">
                <span className="font-jost">
                  Ensure the security of his digital assets.
                </span>
              </li>
            </ul>
          </div>
          <div className="w-full md:w-1/2 lg:w-5/12">
            <h4 className="text-lg md:text-3xl font-bold text-white pb-6 xl:pb-10">
              <span className="font-jost">Pain Points</span>
            </h4>
            <ul className="list-none mb-0">
              <li className="text-lg font-normal text-white pb-7 md:pb-8 xl:pb-10">
                <span className="font-jost">Managing multiple trades.</span>
              </li>
              <li className="text-lg font-normal text-white pb-7 md:pb-8 xl:pb-10">
                <span className="font-jost">
                  Navigating complex interfaces.
                </span>
              </li>
              <li className="text-lg font-normal text-white pb-7 md:pb-8 xl:pb-10">
                <span className="font-jost">
                  Ensuring the security of his assets.
                </span>
              </li>
              <li className="text-lg font-normal text-white">
                <span className="font-jost">
                  Staying informed about cryptocurrency.
                </span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </Container>
  </Container>
);

export default About;
